import { useCallback, useMemo, useState, useEffect, forwardRef, useImperativeHandle, useRef } from 'react';
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  addEdge,
  Connection,
  Node,
  Edge,
  BackgroundVariant,
  Panel,
  useReactFlow,
  ReactFlowProvider,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import type { UnifiedDiagramData, UnifiedDiagramNode } from '@veodiagram/shared';
import { DeploymentNode } from '../nodes/DeploymentNode';
import { CodeBlockNode } from '../nodes/CodeBlockNode';
import { StorageNode } from '../nodes/StorageNode';
import { QueueNode } from '../nodes/QueueNode';
import { ServiceNode } from '../nodes/ServiceNode';
import { SmartBezierEdge, SmartSmoothStepEdge } from '../edges';
// Issue #414: Import call graph node types for multi-handle support
import { callGraphNodeTypes } from './callGraph/CallGraphNodes';
// Issue #500: Import graph node types
// Issue #565: Import group node type for grouped graph view
import { importGraphNodeTypes, importGroupNodeTypes } from './importGraph';
// Schema diagram node types (EntityNode)
import { schemaNodeTypes } from './schema/schemaFlowConverter';
import { CrowsFootEdge } from './schema/CrowsFootEdge';
import {
  convertToReactFlow,
  convertFromReactFlow,
} from './shared/reactFlowConverter';
import { applyLayout, LayoutDirection, LayoutAlgorithm } from './shared/layoutEngine';
import { DiagramControlsProvider } from '../controls';
import type { DiagramCapabilities, FilterDefinition } from '../controls';

interface ReactFlowRendererProps {
  data: UnifiedDiagramData;
  className?: string;
  readOnly?: boolean;
  onDataChange?: (data: UnifiedDiagramData) => void;
  onNodeClick?: (node: Node) => void;
  onEdgeClick?: (edge: Edge) => void;
  /** When true, hides the directory filter in the toolbar (used by ImportGraphPage which has its own directory filter) */
  hideDirectoryFilter?: boolean;
}

export interface ReactFlowRendererHandle {
  fitView: () => void;
  zoomIn: () => void;
  zoomOut: () => void;
  getViewportElement: () => HTMLElement | null;
  applyAutoLayout: () => Promise<void>;
}

// Custom node types
// Issue #414: Added call graph node types for multi-handle support
const nodeTypes = {
  deployment: DeploymentNode,
  codeBlock: CodeBlockNode,
  storage: StorageNode,
  queue: QueueNode,
  service: ServiceNode,
  // Call graph node types with multi-handle support
  ...callGraphNodeTypes,
  // Import graph node types
  ...importGraphNodeTypes,
  // Import group node (collapsible container, Issue #565)
  ...importGroupNodeTypes,
  // Schema diagram node types (EntityNode)
  ...schemaNodeTypes,
};

// Custom edge types with smart label positioning
const edgeTypes = {
  smartBezier: SmartBezierEdge,
  smartSmoothStep: SmartSmoothStepEdge,
  // Schema diagram Crow's Foot notation edges
  crowsFoot: CrowsFootEdge,
};

// ============= Helper Functions =============

/**
 * Extract directory from a path string.
 * Returns top-level directory (first 2 segments) for grouping.
 */
function extractDirectoryFromPath(path: string): string | null {
  const parts = path.split('/');
  if (parts.length > 1) {
    // Simplify to top-level directories for grouping (first 2 segments)
    if (parts.length > 2) {
      return parts.slice(0, 2).join('/');
    }
    // For paths with only 2 parts, use the parent directory
    return parts.slice(0, -1).join('/');
  }
  return null;
}

/**
 * Extract directory groups from node labels/data.
 * Issue #388: Also checks node.data.module for call graph nodes where
 * the label is just the function name and module contains the file path.
 */
function extractDirectoryGroups(nodes: UnifiedDiagramNode[]): Map<string, string[]> {
  const groups = new Map<string, string[]>();

  nodes.forEach(node => {
    let directory = 'Other';

    // First, try to extract from label (for component dependency diagrams)
    const label = node.label || '';
    const labelDirectory = extractDirectoryFromPath(label);

    if (labelDirectory) {
      directory = labelDirectory;
    } else {
      // For call graph nodes, check the module field in node.data
      const modulePath = node.data?.module as string | undefined;
      if (modulePath) {
        const moduleDirectory = extractDirectoryFromPath(modulePath);
        if (moduleDirectory) {
          directory = moduleDirectory;
        } else if (modulePath && !modulePath.includes('/')) {
          // Single-segment module name (e.g., "utils") - use as-is
          directory = modulePath;
        }
      }
    }

    if (!groups.has(directory)) {
      groups.set(directory, []);
    }
    groups.get(directory)!.push(node.id);
  });

  return groups;
}

/**
 * Get connected node IDs for focus mode (ego network)
 */
function getConnectedNodeIds(
  nodeId: string,
  _nodes: UnifiedDiagramNode[],
  edges: { source: string; target: string }[],
  hops: number = 1
): Set<string> {
  const connected = new Set<string>([nodeId]);

  for (let i = 0; i < hops; i++) {
    const currentNodes = Array.from(connected);
    currentNodes.forEach(nid => {
      edges.forEach(edge => {
        if (edge.source === nid) connected.add(edge.target);
        if (edge.target === nid) connected.add(edge.source);
      });
    });
  }

  return connected;
}

/**
 * Filter nodes based on search query
 */
function filterNodesBySearch(nodes: Node[], query: string): Set<string> {
  if (!query.trim()) return new Set(nodes.map(n => n.id));

  const lowerQuery = query.toLowerCase();
  const matched = new Set<string>();

  nodes.forEach(node => {
    const label = String(node.data?.label || '').toLowerCase();
    const nodeType = String(node.data?.nodeType || '').toLowerCase();
    if (label.includes(lowerQuery) || nodeType.includes(lowerQuery)) {
      matched.add(node.id);
    }
  });

  return matched;
}

/**
 * Build filter definitions for ReactFlowRenderer
 * Issue #245: Create dynamic filters based on diagram content
 */
function buildFilterDefinitions(
  _nodes: Node[],
  directoryGroups: Map<string, string[]>
): FilterDefinition[] {
  const filters: FilterDefinition[] = [];

  // Directory filter
  if (directoryGroups.size > 1) {
    const directoryOptions = Array.from(directoryGroups.entries())
      .sort((a, b) => b[1].length - a[1].length)
      .map(([dir, nodeIds]) => ({
        value: dir,
        label: `${dir} (${nodeIds.length})`,
      }));

    filters.push({
      id: 'directory',
      label: 'Directory',
      type: 'dropdown',
      options: directoryOptions,
      getNodeValue: (node: Node) => {
        // First try label (for component dependency diagrams)
        const label = String(node.data?.label || '');
        const labelDirectory = extractDirectoryFromPath(label);
        if (labelDirectory) {
          return labelDirectory;
        }

        // For call graph nodes, check the module field
        const modulePath = node.data?.module as string | undefined;
        if (modulePath) {
          const moduleDirectory = extractDirectoryFromPath(modulePath);
          if (moduleDirectory) {
            return moduleDirectory;
          }
          // Single-segment module name
          if (!modulePath.includes('/')) {
            return modulePath;
          }
        }

        return 'Other';
      },
    });
  }

  return filters;
}

/**
 * Get capabilities for ReactFlowRenderer
 */
function getReactFlowCapabilities(
  filters: FilterDefinition[],
  hasFocusMode: boolean
): DiagramCapabilities {
  return {
    search: true,
    focus: hasFocusMode,
    filters,
    layoutDirection: true,
    autoLayout: true,
    legend: false,
    interactive: true,
    zoomPan: true,
  };
}

// ============= Sub-components =============

interface DiagramToolbarProps {
  // Search & Filter
  searchQuery: string;
  onSearchChange: (query: string) => void;
  // Focus Mode
  focusedNodeId: string | null;
  onClearFocus: () => void;
  focusHops: number;
  onFocusHopsChange: (hops: number) => void;
  // Directory Groups
  directoryGroups: Map<string, string[]>;
  selectedDirectory: string | null;
  onDirectoryChange: (dir: string | null) => void;
  // Layout Direction
  layoutDirection: LayoutDirection;
  onLayoutDirectionChange: (dir: LayoutDirection) => void;
  // Layout Algorithm
  layoutAlgorithm: LayoutAlgorithm;
  onLayoutAlgorithmChange: (algo: LayoutAlgorithm) => void;
  // Actions
  onAutoLayout: () => void;
  onFitView: () => void;
  isLayouting: boolean;
  // Stats
  totalNodes: number;
  visibleNodes: number;
  hideDirectoryFilter?: boolean;
}

function DiagramToolbar({
  searchQuery,
  onSearchChange,
  focusedNodeId,
  onClearFocus,
  focusHops,
  onFocusHopsChange,
  directoryGroups,
  selectedDirectory,
  onDirectoryChange,
  layoutDirection,
  onLayoutDirectionChange,
  layoutAlgorithm,
  onLayoutAlgorithmChange,
  onAutoLayout,
  onFitView,
  isLayouting,
  totalNodes,
  visibleNodes,
  hideDirectoryFilter,
}: DiagramToolbarProps) {
  const [isExpanded, setIsExpanded] = useState(true);

  return (
    <div className="bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
      {/* Header */}
      <div
        className="flex items-center justify-between px-3 py-2 bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
          Diagram Controls
        </span>
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-500 dark:text-slate-400">
            {visibleNodes}/{totalNodes} nodes
          </span>
          <svg
            className={`w-4 h-4 text-slate-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>

      {isExpanded && (
        <div className="p-3 space-y-3">
          {/* Search */}
          <div>
            <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
              Search Nodes
            </label>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder="Search by name..."
              className="w-full px-2 py-1.5 text-sm border border-slate-200 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 placeholder-slate-400"
            />
          </div>

          {/* Focus Mode */}
          {focusedNodeId && (
            <div className="p-2 bg-blue-50 dark:bg-blue-900/30 rounded-md">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-blue-700 dark:text-blue-300">
                  Focus Mode Active
                </span>
                <button
                  onClick={onClearFocus}
                  className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                >
                  Clear
                </button>
              </div>
              <div className="flex items-center gap-2">
                <label className="text-xs text-blue-600 dark:text-blue-400">Hops:</label>
                <select
                  value={focusHops}
                  onChange={(e) => onFocusHopsChange(Number(e.target.value))}
                  className="text-xs px-1.5 py-0.5 border border-blue-200 dark:border-blue-700 rounded bg-white dark:bg-slate-800"
                >
                  <option value={1}>1 (direct)</option>
                  <option value={2}>2</option>
                  <option value={3}>3</option>
                </select>
              </div>
            </div>
          )}

          {/* Directory Filter — hidden when parent manages directory filtering (e.g. ImportGraphPage) */}
          {!hideDirectoryFilter && (
          <div>
            <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
              Filter by Directory
            </label>
            <select
              value={selectedDirectory || ''}
              onChange={(e) => onDirectoryChange(e.target.value || null)}
              className="w-full px-2 py-1.5 text-sm border border-slate-200 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
            >
              <option value="">All directories</option>
              {Array.from(directoryGroups.entries())
                .sort((a, b) => b[1].length - a[1].length)
                .map(([dir, nodeIds]) => (
                  <option key={dir} value={dir}>
                    {dir} ({nodeIds.length})
                  </option>
                ))}
            </select>
          </div>
          )}

          {/* Layout Direction */}
          <div>
            <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
              Layout Direction
            </label>
            <div className="flex gap-1">
              {(['TB', 'LR', 'BT', 'RL'] as LayoutDirection[]).map((dir) => (
                <button
                  key={dir}
                  onClick={() => onLayoutDirectionChange(dir)}
                  className={`flex-1 px-2 py-1 text-xs rounded ${
                    layoutDirection === dir
                      ? 'bg-blue-500 text-white'
                      : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
                  }`}
                  title={
                    dir === 'TB' ? 'Top to Bottom' :
                    dir === 'LR' ? 'Left to Right' :
                    dir === 'BT' ? 'Bottom to Top' :
                    'Right to Left'
                  }
                >
                  {dir}
                </button>
              ))}
            </div>
          </div>

          {/* Layout Algorithm */}
          <div>
            <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
              Layout Algorithm
            </label>
            <div className="flex flex-wrap gap-1">
              {([
                { value: 'dagre' as LayoutAlgorithm, label: 'Hierarchical' },
                { value: 'elk' as LayoutAlgorithm, label: 'Architecture' },
                { value: 'force' as LayoutAlgorithm, label: 'Cluster' },
                { value: 'radial' as LayoutAlgorithm, label: 'Radial' },
                { value: 'tree' as LayoutAlgorithm, label: 'Tree' },
              ]).map(({ value, label }) => (
                <button
                  key={value}
                  onClick={() => onLayoutAlgorithmChange(value)}
                  className={`px-2 py-1 text-xs rounded ${
                    layoutAlgorithm === value
                      ? 'bg-blue-500 text-white'
                      : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2 pt-2 border-t border-slate-200 dark:border-slate-700">
            <button
              onClick={onAutoLayout}
              disabled={isLayouting}
              className="flex-1 px-3 py-1.5 text-sm bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLayouting ? 'Layouting...' : 'Auto Layout'}
            </button>
            <button
              onClick={onFitView}
              className="px-3 py-1.5 text-sm bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-md hover:bg-slate-200 dark:hover:bg-slate-600"
            >
              Fit View
            </button>
          </div>

          {/* Help Text */}
          <p className="text-xs text-slate-500 dark:text-slate-400 italic">
            Tip: Double-click a node to focus on its connections
          </p>
        </div>
      )}
    </div>
  );
}

// ============= Main Component =============

function ReactFlowRendererInner(
  {
    data,
    className = '',
    readOnly = false,
    onDataChange,
    onNodeClick,
    onEdgeClick,
    hideDirectoryFilter = false,
  }: ReactFlowRendererProps,
  ref: React.Ref<ReactFlowRendererHandle>
) {
  const reactFlowInstance = useReactFlow();
  const containerRef = useRef<HTMLDivElement>(null);

  // Layout state
  const [isLayouting, setIsLayouting] = useState(false);
  const [layoutDirection, setLayoutDirection] = useState<LayoutDirection>('TB');
  const [layoutAlgorithm, setLayoutAlgorithm] = useState<LayoutAlgorithm>('dagre');

  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDirectory, setSelectedDirectory] = useState<string | null>(null);

  // Focus mode state
  const [focusedNodeId, setFocusedNodeId] = useState<string | null>(null);
  const [focusHops, setFocusHops] = useState(1);

  // Convert unified data to React Flow format
  const { nodes: initialNodes, edges: initialEdges } = useMemo(
    () => convertToReactFlow(data),
    [data]
  );

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  // Extract directory groups
  const directoryGroups = useMemo(() => extractDirectoryGroups(data.nodes), [data.nodes]);

  // Calculate visible nodes based on filters
  const visibleNodeIds = useMemo(() => {
    let visible = new Set(data.nodes.map(n => n.id));

    // Apply search filter
    if (searchQuery.trim()) {
      const searchMatches = filterNodesBySearch(nodes, searchQuery);
      visible = new Set([...visible].filter(id => searchMatches.has(id)));
    }

    // Apply directory filter
    if (selectedDirectory) {
      const dirNodes = directoryGroups.get(selectedDirectory) || [];
      visible = new Set([...visible].filter(id => dirNodes.includes(id)));
    }

    // Apply focus mode
    if (focusedNodeId) {
      const focusedNodes = getConnectedNodeIds(focusedNodeId, data.nodes, data.edges, focusHops);
      visible = new Set([...visible].filter(id => focusedNodes.has(id)));
    }

    return visible;
  }, [data.nodes, data.edges, searchQuery, selectedDirectory, focusedNodeId, focusHops, nodes, directoryGroups]);

  // Filter nodes and edges for display
  const { filteredNodes, filteredEdges } = useMemo(() => {
    const filteredNodes = nodes.map(node => ({
      ...node,
      hidden: !visibleNodeIds.has(node.id),
      style: {
        ...node.style,
        opacity: visibleNodeIds.has(node.id) ? 1 : 0.15,
      },
    }));

    const filteredEdges = edges.map(edge => ({
      ...edge,
      hidden: !visibleNodeIds.has(edge.source) || !visibleNodeIds.has(edge.target),
      style: {
        ...edge.style,
        opacity: (visibleNodeIds.has(edge.source) && visibleNodeIds.has(edge.target)) ? 1 : 0.1,
      },
    }));

    return { filteredNodes, filteredEdges };
  }, [nodes, edges, visibleNodeIds]);

  // Update nodes and edges when data changes
  useEffect(() => {
    const { nodes: newNodes, edges: newEdges } = convertToReactFlow(data);
    setNodes(newNodes);
    setEdges(newEdges);
  }, [data, setNodes, setEdges]);

  // Handle new connections
  const onConnect = useCallback(
    (connection: Connection) => {
      if (readOnly) return;
      setEdges((eds) => addEdge(connection, eds));
    },
    [setEdges, readOnly]
  );

  // Notify parent of changes
  const notifyChange = useCallback(() => {
    if (onDataChange) {
      const updatedData = convertFromReactFlow(nodes, edges, data.metadata);
      onDataChange(updatedData);
    }
  }, [nodes, edges, data.metadata, onDataChange]);

  // Handle node drag end
  const onNodeDragStop = useCallback(() => {
    if (!readOnly) {
      notifyChange();
    }
  }, [readOnly, notifyChange]);

  // Handle node click
  const handleNodeClick = useCallback(
    (_: React.MouseEvent, node: Node) => {
      onNodeClick?.(node);
    },
    [onNodeClick]
  );

  // Handle node double-click for focus mode
  const handleNodeDoubleClick = useCallback(
    (_: React.MouseEvent, node: Node) => {
      if (focusedNodeId === node.id) {
        // Toggle off if already focused
        setFocusedNodeId(null);
      } else {
        setFocusedNodeId(node.id);
      }
      // Zoom to the node
      setTimeout(() => {
        reactFlowInstance.fitView({
          padding: 0.3,
          nodes: [node],
          duration: 300,
        });
      }, 50);
    },
    [focusedNodeId, reactFlowInstance]
  );

  // Handle edge click
  const handleEdgeClick = useCallback(
    (_: React.MouseEvent, edge: Edge) => {
      onEdgeClick?.(edge);
    },
    [onEdgeClick]
  );

  // Apply auto layout with current direction and algorithm.
  // Issue #561: Only layout the currently-visible nodes so that direction/algorithm
  // changes on a small filtered view are fast regardless of total graph size.
  const applyAutoLayout = useCallback(async () => {
    setIsLayouting(true);
    try {
      const algorithm = layoutAlgorithm;

      const updatedMetadata = {
        ...data.metadata,
        layoutAlgorithm: algorithm,
        layoutDirection: layoutDirection,
      };

      // Only layout nodes that are currently visible (pass all active filters).
      // Hidden nodes keep their existing positions and are merged back afterwards.
      const nodesToLayout = data.nodes.filter(n => visibleNodeIds.has(n.id));
      const edgesToLayout = data.edges.filter(
        e => visibleNodeIds.has(e.source) && visibleNodeIds.has(e.target)
      );

      console.log('[AutoLayout] algorithm:', algorithm, '— layouting', nodesToLayout.length, 'of', data.nodes.length, 'nodes');

      const layoutedVisible = await applyLayout(nodesToLayout, edgesToLayout, updatedMetadata);

      // Merge layouted positions back into the full node set.
      const posMap = new Map(layoutedVisible.map(n => [n.id, n]));
      const allLayouted = data.nodes.map(n => posMap.get(n.id) ?? n);

      const { nodes: newNodes, edges: newEdges } = convertToReactFlow(
        { ...data, nodes: allLayouted },
        false
      );

      setNodes(newNodes);
      setEdges(newEdges);

      setTimeout(() => {
        reactFlowInstance.fitView({ padding: 0.2 });
      }, 50);

      if (onDataChange) {
        onDataChange({
          metadata: updatedMetadata,
          nodes: allLayouted,
          edges: data.edges,
        });
      }
    } catch (error) {
      console.error('Failed to apply layout:', error);
    } finally {
      setIsLayouting(false);
    }
  }, [data, layoutDirection, layoutAlgorithm, visibleNodeIds, setNodes, setEdges, reactFlowInstance, onDataChange]);

  // Handle layout direction change
  const handleLayoutDirectionChange = useCallback((dir: LayoutDirection) => {
    setLayoutDirection(dir);
  }, []);

  // Track previous layout direction and algorithm to detect changes
  const prevLayoutDirectionRef = useRef<LayoutDirection>(layoutDirection);
  const prevLayoutAlgorithmRef = useRef<LayoutAlgorithm>(layoutAlgorithm);

  // Re-layout when direction changes
  useEffect(() => {
    if (prevLayoutDirectionRef.current !== layoutDirection) {
      prevLayoutDirectionRef.current = layoutDirection;
      applyAutoLayout();
    }
  }, [layoutDirection, applyAutoLayout]);

  // Re-layout when algorithm changes
  useEffect(() => {
    if (prevLayoutAlgorithmRef.current !== layoutAlgorithm) {
      prevLayoutAlgorithmRef.current = layoutAlgorithm;
      applyAutoLayout();
    }
  }, [layoutAlgorithm, applyAutoLayout]);

  // Clear focus mode
  const clearFocus = useCallback(() => {
    setFocusedNodeId(null);
    setTimeout(() => {
      reactFlowInstance.fitView({ padding: 0.2 });
    }, 50);
  }, [reactFlowInstance]);

  // Expose methods via ref
  useImperativeHandle(ref, () => ({
    fitView: () => reactFlowInstance.fitView({ padding: 0.2 }),
    zoomIn: () => reactFlowInstance.zoomIn(),
    zoomOut: () => reactFlowInstance.zoomOut(),
    getViewportElement: () => containerRef.current?.querySelector('.react-flow__viewport') as HTMLElement | null,
    applyAutoLayout,
  }));

  return (
    <div ref={containerRef} className={`h-full w-full ${className}`}>
      <ReactFlow
        nodes={filteredNodes}
        edges={filteredEdges}
        onNodesChange={readOnly ? undefined : onNodesChange}
        onEdgesChange={readOnly ? undefined : onEdgesChange}
        onConnect={onConnect}
        onNodeDragStop={onNodeDragStop}
        onNodeClick={handleNodeClick}
        onNodeDoubleClick={handleNodeDoubleClick}
        onEdgeClick={handleEdgeClick}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        defaultEdgeOptions={{
          type: 'smartSmoothStep',
        }}
        fitView
        snapToGrid
        snapGrid={[15, 15]}
        nodesDraggable={!readOnly}
        nodesConnectable={!readOnly}
        elementsSelectable={!readOnly}
        className="bg-slate-50 dark:bg-slate-900"
      >
        <Background variant={BackgroundVariant.Dots} gap={15} size={1} />
        <Controls showInteractive={!readOnly} />
        <MiniMap
          nodeStrokeWidth={3}
          zoomable
          pannable
          className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md"
        />

        {/* Diagram Toolbar - Issue #231, #244: Integrated component type filter */}
        <Panel position="top-left" className="max-w-xs z-10 mt-2 ml-2">
          <DiagramToolbar
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            focusedNodeId={focusedNodeId}
            onClearFocus={clearFocus}
            focusHops={focusHops}
            onFocusHopsChange={setFocusHops}
            directoryGroups={directoryGroups}
            selectedDirectory={selectedDirectory}
            onDirectoryChange={setSelectedDirectory}
            layoutDirection={layoutDirection}
            onLayoutDirectionChange={handleLayoutDirectionChange}
            layoutAlgorithm={layoutAlgorithm}
            onLayoutAlgorithmChange={setLayoutAlgorithm}
            onAutoLayout={applyAutoLayout}
            onFitView={() => reactFlowInstance.fitView({ padding: 0.2 })}
            isLayouting={isLayouting}
            totalNodes={data.nodes.length}
            visibleNodes={visibleNodeIds.size}
            hideDirectoryFilter={hideDirectoryFilter}
          />
        </Panel>
      </ReactFlow>
    </div>
  );
}

// Wrap with forwardRef
const ReactFlowRendererWithRef = forwardRef(ReactFlowRendererInner);

/**
 * Wrapper component that provides DiagramControlsContext
 * Issue #245: Integrate with unified diagram controls
 */
const ReactFlowRendererWithControls = forwardRef<ReactFlowRendererHandle, ReactFlowRendererProps>(
  (props, ref) => {
    const { data } = props;
    const internalRef = useRef<ReactFlowRendererHandle>(null);

    // Forward ref
    useImperativeHandle(ref, () => ({
      fitView: () => internalRef.current?.fitView(),
      zoomIn: () => internalRef.current?.zoomIn(),
      zoomOut: () => internalRef.current?.zoomOut(),
      getViewportElement: () => internalRef.current?.getViewportElement() ?? null,
      applyAutoLayout: async () => internalRef.current?.applyAutoLayout(),
    }));

    // Compute filter definitions based on data
    const directoryGroups = useMemo(() => extractDirectoryGroups(data.nodes), [data.nodes]);

    // Temporary conversion for filter building (we'll use the actual nodes from the inner component)
    const { nodes: tempNodes } = useMemo(() => convertToReactFlow(data), [data]);
    const filterDefinitions = useMemo(
      () => buildFilterDefinitions(tempNodes, directoryGroups),
      [tempNodes, directoryGroups]
    );

    const capabilities = useMemo(
      () => getReactFlowCapabilities(filterDefinitions, true),
      [filterDefinitions]
    );

    return (
      <ReactFlowProvider>
        <DiagramControlsProvider
          initialCapabilities={capabilities}
          onFitView={() => internalRef.current?.fitView()}
          onZoomIn={() => internalRef.current?.zoomIn()}
          onZoomOut={() => internalRef.current?.zoomOut()}
          onAutoLayout={() => internalRef.current?.applyAutoLayout()}
        >
          <ReactFlowRendererWithRef {...props} ref={internalRef} />
        </DiagramControlsProvider>
      </ReactFlowProvider>
    );
  }
);

ReactFlowRendererWithControls.displayName = 'ReactFlowRendererWithControls';

// Export wrapped in provider
export const ReactFlowRenderer = ReactFlowRendererWithControls;
